import { isNil } from 'lodash';
import { createSelector } from 'reselect';
import { ArgeementIDtuple } from 'src/types';
import { ApplicationState } from '../types';
import { AutomaticInvestmentsState } from './types';

const slice = ({ automaticInvestments }: ApplicationState) =>
  automaticInvestments;

export const getRecurringPurchases = createSelector(
  [slice],
  ({ systematicPurchases }) => systematicPurchases || {}
);

export const getAutoExchanges = createSelector(
  [slice],
  ({ systematicExchanges = undefined }) => systematicExchanges || {}
);

export const getRecurringRedemptions = createSelector(
  [slice],
  ({ systematicRedemptions = undefined }) => systematicRedemptions || {}
);

export const getAutoMax = createSelector(
  [slice],
  ({ autoMaxTransactions = undefined }) => autoMaxTransactions || {}
);

export const getUserAutoMaxPOAs = createSelector(
  [slice],
  ({ autoMaxPOAs = undefined }) => autoMaxPOAs || {}
);
export const getAutoMaxAsArray = createSelector([getAutoMax], (autoMaxes) => {
  return Object.values(autoMaxes);
});
/**
 * Complex selector to extract the agreement map corresponding to the selected bank. or a 'default' agreement map
 * with empty lookups if no bank is selected.
 * @param state
 * @returns {AgreementMapEx}
 */
export const getHasRecurringPurchases = createSelector(
  getRecurringPurchases,
  (recurringPurchases) =>
    !isNil(recurringPurchases) &&
    typeof recurringPurchases === 'object' &&
    Object.keys(recurringPurchases).length > 0
);

export const getHasAutoExchanges = createSelector(
  [getAutoExchanges],
  (autoExchanges) =>
    !isNil(autoExchanges) &&
    typeof autoExchanges === 'object' &&
    Object.keys(autoExchanges).length > 0
);

export const getHasRecurringRedemptions = createSelector(
  getRecurringRedemptions,
  (recurringRedemptions) =>
    !isNil(recurringRedemptions) &&
    typeof recurringRedemptions === 'object' &&
    Object.keys(recurringRedemptions).length > 0
);

export const getHasAutoMaxTransactions = createSelector(
  getAutoMax,
  (autoMax) =>
    !isNil(autoMax) &&
    typeof autoMax === 'object' &&
    Object.keys(autoMax).length > 0
);

export const getUserHasAutoMaxPOAs = createSelector(
  getUserAutoMaxPOAs,
  (poas) =>
    !isNil(poas) && typeof poas === 'object' && Object.keys(poas).length > 0
);

export const getAllMonthsAndDays = createSelector(getAutoMax, (autoMax) => ({
  selectedMonths: autoMax.selectedMonths,
  selectedDays: autoMax.selectedDates,
}));

export const getAutomaticTransactions = createSelector(
  getAutoMax,
  getAutoExchanges,
  getRecurringPurchases,
  getRecurringRedemptions,
  (autoMax, autoExchanges, recurringPurchases, recurringRedemptions) => ({
    autoMax,
    autoExchanges,
    recurringPurchases,
    recurringRedemptions,
  })
);

export const isRecurringExchangeTuple =
  (autoExchanges: AutomaticInvestmentsState['systematicExchanges']) =>
  ([fromFundID, toFundID]: ArgeementIDtuple) => {
    return (
      autoExchanges &&
      Object.entries(autoExchanges).some(
        ([fromAgreementID, systematicExchange]) =>
          fromAgreementID === fromFundID &&
          systematicExchange.some(({ targetAgreementKey: { agreementID } }) => {
            return agreementID === toFundID;
          })
      )
    );
  };
